/**
 * "Print only this one document" helper, shared by every full-document view that must be printable
 * on its own — the doctor's Patient Summary tab and the patient portal's visit summary — without
 * disturbing Ctrl+P anywhere else in the app.
 *
 * Printing is switched on by adding a class to <body> (removed again on the browser's `afterprint`
 * event, or by calling `end()` directly — e.g. on unmount, so leaving the screen mid-print never
 * leaves the app stuck in print mode) plus a "print root" class on the one element to keep:
 *  * With :has() (current Chrome/Edge/Safari/Firefox): everything not containing the print root is
 *    removed from the layout, and the print root's ancestors are flattened (static, block, no height
 *    limits or overflow clipping) so the document flows across as many pages as it needs. Only the
 *    ancestors and the print root are touched — never the print root's own internal layout.
 *  * Without :has() the whole second block is dropped (an unknown selector invalidates the rule),
 *    and the first block's fallback applies: hide everything else by visibility and pin the print
 *    root to the page.
 */
export type ScopedPrint = {
  /** Class added to <body> only while printing — never assume it is still set once printing ends. */
  printBodyClass: string
  /** Class for the single element (and everything inside it) that should print. */
  printRootClass: string
  /** <style> text implementing the scoping — render it once, anywhere the print root can appear. */
  css: string
  /** Marks the body as printing, opens the native print dialog, and cleans up on `afterprint`. */
  print: () => void
  /** Removes the body class and cancels any pending `afterprint` listener — call this on unmount too. */
  end: () => void
}

export const createScopedPrint = (name: string): ScopedPrint => {
  const printBodyClass = `printing-${name}`
  const printRootClass = `${name}-print`

  const css = `@media print {
  body.${printBodyClass} * { visibility: hidden !important; }
  body.${printBodyClass} .${printRootClass},
  body.${printBodyClass} .${printRootClass} * { visibility: visible !important; }
  body.${printBodyClass} .${printRootClass} { position: fixed !important; inset: 0 !important; border: 0 !important; }
}
@media print {
  body.${printBodyClass} *:not(:has(.${printRootClass})):not(.${printRootClass}):not(.${printRootClass} *) {
    display: none !important;
  }
  body.${printBodyClass},
  body.${printBodyClass} *:has(.${printRootClass}),
  body.${printBodyClass} .${printRootClass} {
    display: block !important;
    position: static !important;
    inset: auto !important;
    overflow: visible !important;
    height: auto !important;
    min-height: 0 !important;
    max-height: none !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0 !important;
  }
}`

  const end = () => {
    document.body.classList.remove(printBodyClass)
    window.removeEventListener('afterprint', end)
  }

  const print = () => {
    document.body.classList.add(printBodyClass)
    window.addEventListener('afterprint', end)
    window.print()
  }

  return { css, end, print, printBodyClass, printRootClass }
}
